import {
  Typography,
  MenuItem,
  Box,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  LeadBox,
  CustomSelect,
  LeadGrid,
  CustomDatePicker,
  Button,
} from "../../components";
import { useHome } from "./hook";
import { User } from "../../model/User";
import Grid from "@mui/material/Unstable_Grid2";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InboxIcon from "@mui/icons-material/Inbox";
import { ChartCarrousel } from "../../components/Charts/ChartCarrousel";

export const Home = () => {
  const {
    metrics,
    dashboards,
    selectedDashboard,
    dashboardValue,
    isMetricsLoading,
    isLeadsLoading,
    hideMetrics,
    hideTables,
    columnAccess,
    moveToDashboard,
    page,
    pageSize,
    sortModel,
    onPageChange,
    onPageSizeChange,
    onChangeOrder,
    // Chart-related props
    chartSources,
    selectedChartOption,
    sourceOptions,
    chartStartDate,
    chartEndDate,
    setChartStartDate,
    setChartEndDate,
    fetchChartSources,
    setSelectedChartOption,
  } = useHome();

  const user = new User();

  const hasMetrics = metrics.length > 0;
  const hasDashboards = dashboards.length > 0;
  const hasHomeContent = hasMetrics || hasDashboards;

  const metricsDone = hideMetrics || !isMetricsLoading;
  const tablesDone = hideTables || !isLeadsLoading;

  const isDoneLoading = metricsDone && tablesDone;
  const showGlobalEmptyState = isDoneLoading && !hasHomeContent;

  const EmptyState = ({ message }: { message: string }) => (
    <Box
      sx={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Box
        sx={{
          px: 4,
          py: 3,
          borderRadius: 3,
          backgroundColor: "#F7FAFD",
          border: "1px solid #E3ECF5",
          boxShadow: "0px 4px 12px rgba(0,0,0,0.06)",
          maxWidth: 420,
          textAlign: "center",
        }}
      >
        <InboxIcon sx={{ fontSize: 32, color: "#5B8FD8", mb: 1 }} />
        <Typography sx={{ color: "#3A4A5E", fontWeight: 500 }}>
          {message}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {/* HEADER */}
      <Grid container sx={{ mb: 2 }}>
        <Grid xs={12}>
          <Typography
            variant="h4"
            sx={{ color: "#1BA0E9", fontWeight: 600, fontSize: 25 }}
          >
            Hello {user.DisplayUserName}!
          </Typography>
        </Grid>
      </Grid>

      {/* GLOBAL EMPTY STATE */}
      {showGlobalEmptyState && (
        <EmptyState message="Your home page has no items configured. Please set your preferences in the 'Edit Profile' page or contact your administrator." />
      )}

      {!showGlobalEmptyState && chartSources && chartSources.length > 0 && (
        <Accordion
          sx={{
            mb: 3,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            backgroundColor: "background.paper",
            "&:before": {
              display: "none",
            },
            overflow: "hidden",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "primary.light",
              color: "primary.contrastText",
              "& .MuiAccordionSummary-content": {
                justifyContent: "center",
              },
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Analytics Overview
            </Typography>
          </AccordionSummary>

          <AccordionDetails
            sx={{
              backgroundColor: "#fafafa",
              p: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                my: 4,
                gap: 4,
                flexWrap: "wrap",
              }}
            >
              <CustomSelect
                label="Select a source"
                value={selectedChartOption}
                onChange={(event) =>
                  setSelectedChartOption(event.target.value as string)
                }
              >
                <MenuItem value={"0"}>Select a source</MenuItem>
                {sourceOptions.map((option, index) => (
                  <MenuItem key={index} value={option.sourceID.toString()}>
                    {option.sourceSiteName}
                  </MenuItem>
                ))}
              </CustomSelect>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CustomDatePicker
                  label={{ text: "Start date" }}
                  value={chartStartDate}
                  onChange={(newDate) => setChartStartDate(newDate)}
                />
                <CustomDatePicker
                  label={{ text: "End date" }}
                  value={chartEndDate}
                  onChange={(newDate) => setChartEndDate(newDate)}
                />
              </Box>
              <Button label="Apply changes" onClick={fetchChartSources} />
            </Box>
            <ChartCarrousel chartSources={chartSources} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* LP ACCOUNT GRID */}
      {/*LP MAP GRID will be now available in /dealer-links.*/}
      {/*<LPMapGrid accounts={ecoviewMapLPAccounts()} /> */}

      {/* METRICS */}
      {!showGlobalEmptyState && !hideMetrics && hasMetrics && (
        <Grid container spacing={2} sx={{ my: 3 }}>
          {metrics.map((metric, index) => (
            <Grid xs={12} sm={6} md={3} key={metric.id}>
              <LeadBox
                index={index}
                label={metric.description}
                icon={metric.icon}
                data={metric.value}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* DASHBOARDS */}
      {!showGlobalEmptyState && !hideTables && hasDashboards && (
        <Grid container>
          <Grid xs={12}>
            <Card sx={{ borderRadius: 4 }}>
              <CardContent>
                <Grid container>
                  {dashboards.map((val) => (
                    <Grid
                      key={val.id}
                      md={3}
                      lg={2}
                      onClick={() => moveToDashboard(val.id)}
                    >
                      <Typography
                        sx={{
                          color: "#1BA0E9",
                          fontSize: 14,
                          fontWeight: 600,
                          py: 1,
                          borderRadius: 2,
                          backgroundColor:
                            dashboardValue === val.id
                              ? "rgba(0,0,0,0.07)"
                              : "inherit",
                          textAlign: "center",
                          cursor: "pointer",
                        }}
                      >
                        {val.description}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>

                {selectedDashboard?.items?.length ? (
                  <LeadGrid
                    partialColumns={Object.keys(selectedDashboard.items[0])}
                    renderRefresh
                    loading={isLeadsLoading}
                    callLeads={selectedDashboard.items}
                    page={page}
                    pageSize={pageSize}
                    total={selectedDashboard.quantity}
                    onPageChange={onPageChange}
                    onPageSizeChange={onPageSizeChange}
                    onSortModelChange={onChangeOrder}
                    sortModel={sortModel}
                    columnAccess={columnAccess}
                  />
                ) : (
                  <EmptyState message="No data available for this dashboard." />
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </>
  );
};
